import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ActionsMenu from '../../../../../components/ui/ActionsMenu';
import {
  useCreateData,
  useFetchData,
  useFetchFile,
} from '../../../../../hooks';
import {
  BillOfMaterialRecord,
  BOMReportPDMops,
} from '../../../../../types/BillOfMaterial';
import { BOM_API } from '../../../../../hooks/useBillOfMaterialHooks';
import { isUserCanEdit } from '../../../../../lib/utils';
import { useAuthStore } from '../../../../../store';
import React, { useEffect, useState } from 'react';
import { Button, Dropdown, MenuProps, Space, Spin } from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  PrinterOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import BillOfMaterialInfoLoading from './BillOfMaterialInfoLoading';
import AuditCard from '../../../../../components/AuditCard';
import TrackingChain from '../../../../../components/TrackingChain';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import { cn } from '../../../../../lib/utils';
import BillOfMaterialInfoContent from './BillOfMaterialInfoContent';
import { generatePDFPrint } from '../GeneratePDFPrint';
import BillOfMaterialInfoStatus from './BillOfMaterialInfoStatus';
import { StatusHistory } from '../../../../../types';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import { generatePDMopsPrint } from '../PDMopsPrint';
import axiosInstance from '../../../../../lib/axiosInstance';

const BillOfMaterialInfoPage = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useFetchData<BillOfMaterialRecord>(
    `${BOM_API}/${params.id}`,
  );
  const { data: files } = useFetchFile(
    `${BOM_API}/${params.id}/files`,
    'api/production/boms',
  );
  const { data: statusHistory, isLoading: isLoadingHistory } = useFetchData<
    StatusHistory[]
  >(`${BOM_API}/${params?.id}/status-logs`);

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [spinning, setSpinning] = useState<boolean>(false);
  const [message, setMessage] = useState<string | undefined>('');
  const {
    mutate: mutateRework,
    isSuccess: isSuccessRework,
    data: dataRework,
  } = useCreateData(`${BOM_API}/${params?.id}/rework`);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  useEffect(() => {
    if (isError) setIsErrorModalOpen(isError);
    if (isSuccessRework) {
      setSpinning(false);
      setSuccessModalOpen(true);
      setMessage(`BOM has been rework with revision ${dataRework.code}`);
    }
  }, [isError]);

  const handleClose = () => {
    navigate('/production/bill-of-material');
  };

  const handleRework = () => {
    setInfoModalOpen(true);
  };

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  const items: MenuProps['items'] = [
    {
      label: 'Edit',
      key: 'edit',
      icon: <EditOutlined />,
      disabled:
        data?.status === 'APPROVED' ||
        data?.status === 'READY' ||
        data?.status === 'DONE' ||
        data?.status === 'CANCEL',
    },
    {
      label: 'Download',
      key: 'download',
      icon: <DownloadOutlined />,
      onClick: async () => {
        setShowLoading(true);
        try {
          // Fetch fresh so it never prints blank before the page data loaded.
          const { data: fresh } = await axiosInstance.get<BillOfMaterialRecord>(
            `${BOM_API}/${params.id}`,
          );
          generatePDFPrint({
            data: fresh ?? data,
            attachments: files,
            mode: 'download',
          });
        } finally {
          setShowLoading(false);
        }
      },
    },
    {
      label: 'PDMOPS Print',
      key: 'pdmops_print',
      icon: <PrinterOutlined />,
      onClick: async () => {
        setShowLoading(true);
        try {
          // Resolve the code even before the page's own data has loaded
          // (clicking on first open used to silently do nothing -> "refresh
          // to get data"). Fall back to fetching the BOM by id for its code.
          let code = data?.code;
          if (!code) {
            const { data: bom } = await axiosInstance.get<BillOfMaterialRecord>(
              `${BOM_API}/${params.id}`,
            );
            code = bom?.code;
          }
          if (!code) return;
          const { data: report } = await axiosInstance.get<BOMReportPDMops>(
            `/reports/bom-report/${code}`,
          );
          if (report) await generatePDMopsPrint({ data: report });
        } finally {
          setShowLoading(false);
        }
      },
    },
    {
      label: 'Print',
      key: 'print',
      icon: <PrinterOutlined />,
      onClick: async () => {
        setShowLoading(true);
        try {
          const { data: fresh } = await axiosInstance.get<BillOfMaterialRecord>(
            `${BOM_API}/${params.id}`,
          );
          generatePDFPrint({
            data: fresh ?? data,
            attachments: files,
            mode: 'print',
          });
        } finally {
          setShowLoading(false);
        }
      },
    },
    {
      label: 'Rework',
      key: 'rework',
      icon: <RetweetOutlined />,
      onClick: handleRework,
    },
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'edit') {
      navigate(`/production/bill-of-material/request/${data?.id}`);
    }
  };

  if (isLoading && isLoadingHistory) {
    return <BillOfMaterialInfoLoading active={true} />;
  }

  return (
    <>
      <Spin fullscreen spinning={spinning} />
      <div
        className={cn(
          'flex items-center justify-between',
          isLoading ? 'hidden' : '',
        )}
      >
        <PageHeaderAction
          action={searchParams.get('action')}
          moduleName={'Bill Of Material'}
        />
        <ActionsMenu items={items} onClick={onClick} onBack={handleClose} />
      </div>
      <div className={'flex'}>
        <BillOfMaterialInfoContent
          data={data}
          attachments={files}
          api={BOM_API}
          recordId={params.id}
          canEdit={isUserCanEdit(user?.moduleAccess, 'BillOfMaterial')}
        />
        <div className={'w-3/12'}>
          <BillOfMaterialInfoStatus
            data={data}
            statusHistory={statusHistory !== undefined ? statusHistory : []}
          />
          <div className={'m-4 space-y-4'}>
            <TrackingChain api={BOM_API} id={params.id} />
            <AuditCard
              createdBy={data?.createdBy}
              updatedBy={data?.updatedBy}
              timeCreated={data?.timeCreated}
              timeUpdated={data?.timeUpdated}
            />
          </div>
        </div>
      </div>
      <ErrorModal
        visible={isErrorModalOpen}
        message={'Invalid purchase order'}
        onClose={handleErrorModalClose}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={`Create a revision for BOM ${data?.code}?`}
        onClose={handleInfoModalClose}
        onOk={() => {
          setSpinning(true);
          setInfoModalOpen(false);
          mutateRework({});
        }}
      />
      <InfoModal
        visible={isSuccessModalOpen}
        message={message}
        onClose={handleInfoModalClose}
      />
      <Spin spinning={showLoading} fullscreen />
    </>
  );
};

export default BillOfMaterialInfoPage;
